import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNotify } from "react-admin";
import { IfCanAccess } from "@react-admin/ra-rbac";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import { BASE_API_URL } from "../../../config/configUrl";
import { getAccessToken } from "../../../admin/authProvider";

const postGenerateStatements = (month: string | null) =>
  fetch(`${BASE_API_URL}/gasstation/statements/generate`, {
    method: "POST",
    credentials: "include",
    headers: new Headers({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${getAccessToken()}`,
    }),
    body: JSON.stringify({ month: month || undefined }),
  }).then(async (response) => {
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || response.statusText);
    }
    return response.json();
  });

function GenerateAction() {
  const { t }: { t: any } = useTranslation();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const result = await postGenerateStatements(month || null);
      notify(
        t("Statement generation started for {{month}} (workflow {{workflowId}})", {
          month: result.month,
          workflowId: result.workflowId,
        }),
        { type: "success" }
      );
      setOpen(false);
      setMonth("");
    } catch (error: any) {
      notify(`${t("Action failed")}: ${error.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <IfCanAccess action="generate" resource="gasStationStatements">
      <Button
        variant="text"
        startIcon={<PlayCircleOutlinedIcon />}
        onClick={() => setOpen(true)}
      >
        {t("Generate statements")}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("Generate monthly statements")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("Month (optional)")}
            placeholder="2026-06"
            helperText={t("Leave empty to generate the previous month")}
            fullWidth
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t("Generate")}
          </Button>
        </DialogActions>
      </Dialog>
    </IfCanAccess>
  );
}

export default GenerateAction;
